import {
  Box,
  Button,
  Stack,
  TextField
} from '@mui/material';
import {
  useCallback,
  useMemo,
  useRef
} from 'react';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import {
  BaseDialogLayout,
  ChatBubble
} from 'modules/components/layer02';

import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StyledDropDown } from 'modules/components/layer01';
import { GLAccountAutocomplete } from 'modules/components/layer04';
import * as ds from './dataService';



export type SpecifyGlAccountModalProps = {
  isVisible: boolean;
  handleClose: () => void;
};

export const FC = (props: SpecifyGlAccountModalProps) => {
  const slideRef = useRef();
  const { companyId } = useParams<{ companyId: string }>();

  const form = ds.useQuery();
  const [
    sendMessage,
    sendMessageTuple
  ] = ds.useSendMessageRequestingGlAccountData(companyId ?? '');

  const isValid = useMemo(() => (
    form.formState.glAccountId
    && form.formState.frequency
    && form.formState.start
    && form.formState.end
  ), [form.formState]);



  const handleClickLast5Years = useCallback(() => {
    const start = __getFormattedYearsAgo(5, 'start');
    const end = __getFormattedYearsAgo(0, 'end');
    form.dispatch({
      type: 'BATCH_UPDATE',
      payload: {
        ...form.formState,
        start,
        end
      }
    });
  }, [form]);



  return (
    <BaseDialogLayout.FC
      formTitle='Choose your data and select a timeframe'
      handleClose={props.handleClose}
      isVisible={props.isVisible}
      subTitle={(
        <ChatBubble.FC
          variant='info'
          slideRef={slideRef.current}
          text="👋 I've compiled over 150 data segments to aid in your company analysis. Use the dropdown / search to gain rapid insight."
          role='SHERLOCK'
        />
      )}
      saveText='Search'
      handleSubmit={isValid ? async () => {
        if (form.formState.start && form.formState.end) {
          await sendMessage({
            variables: {
              input: {
                start: DateTime.fromJSDate(form.formState.start).startOf('month'),
                end: DateTime.fromJSDate(form.formState.end).endOf('month'),
                frequency: form.formState.frequency,
                glAccountId: form.formState.glAccountId
              }
            }
          });
          props.handleClose();
        }
      } : undefined}
    >
      <Stack
        spacing={2}
      >
        <GLAccountAutocomplete.FC
          value={form.formState.glAccountId}
          onChange={(id) => {
            form.dispatch({
              type: 'UPDATE',
              payload: {
                property: 'glAccountId',
                value: id ?? ''
              }
            });
          }}
        />

        <Box ref={slideRef}>
          <Button
            variant='outlined'
            onClick={handleClickLast5Years}
          >
            Last 5 years
          </Button>
        </Box>

        <Stack
          direction='row'
          spacing={2}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label='Start'
              views={['year']}
              value={form.formState.start}
              onChange={(val) => {
                form.dispatch({
                  type: 'UPDATE',
                  payload: {
                    property: 'start',
                    value: val
                  }
                });
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label='End'
              views={['year']}
              value={form.formState.end}
              onChange={(val) => {
                form.dispatch({
                  type: 'UPDATE',
                  payload: {
                    property: 'end',
                    value: val
                  }
                });
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Stack>

        <StyledDropDown.FC
          {...form.genFieldProps('frequency')}
          label='Frequency'
          options={[
            {
              id: 'quarterly',
              name: 'quarterly',
              displayName: 'Quarterly'
            },
            {
              id: 'annual',
              name: 'annual',
              displayName: 'Annual'
            }
          ]}
        />
      </Stack>
    </BaseDialogLayout.FC>
  );
};



function __getFormattedYearsAgo(years: number, anchor: 'start' | 'end') {
  const now = DateTime.now().minus({
    years
  });
  const boundary = anchor === 'start' ? now.startOf('year') : now.endOf('year');
  return boundary.toJSDate();
}
